import _ from 'lodash';
import React from 'react';
import styled, { ThemeProvider } from 'styled-components';
import { useDebouncedCallback } from 'use-debounce';
import TextareaAutosize from 'react-autosize-textarea';

import Loading from '../../../../shared/loading';
import theme from '../../../../../assets/stylesheets/_settings.global.scss';
import useUpdateDisclaimer from '../../../../services/wire/disclaimer-service';
import useEditorStore from '../state-hook';
import {
  Button,
  BottomLink,
  Container,
  Link,
  Title,
  TitleContainer,
} from './shared/styles';

const RestoreLink = styled(Link)`
  position: absolute;
  right: 0;
  margin: 0;
`;

const ContentEditable = styled(TextareaAutosize)`
  font-family: ${props => props.theme.fontFamilyBody};
  font-size: 14px;
  font-style: italic;
  line-height: 18px;
  color: ${props => props.theme.secondaryColor};
  border: none;
  padding: 0;
  resize: none;
`;

const Content = styled.div`
  font-family: ${props => props.theme.fontFamilyBody};
  font-size: 14px;
  font-style: italic;
  line-height: 18px;
  color: ${props => props.theme.secondaryColor};
  padding: 0;
  white-space: pre-wrap;
`;

export default function Disclaimer() {
  const [editable, setEditable] = React.useState(false);
  const [deleted, setDeleted] = React.useState(false);
  const set = useEditorStore(state => state.set);
  const wire = useEditorStore(state => state.wire);
  const [originalDisclaimer, setOriginalDisclaimer] = React.useState(_.get(wire, 'disclaimer'));
  const [{ loading }, updateDisclaimer] = useUpdateDisclaimer();

  const { callback: debounced } = useDebouncedCallback((wId, disc) => {
    updateDisclaimer(wId, disc);
  }, 500);

  const loadingRef = React.useRef();
  const contentRef = React.useRef();

  React.useEffect(() => {
    if (editable && contentRef.current) {
      contentRef.current.focus();
    }
  }, [editable]);

  React.useEffect(() => {
    loadingRef.current && loadingRef.current.toggle(loading);
  }, [loading]);

  const toggleEditable = e => {
    e.preventDefault();
    setEditable(!editable);
  };

  const onChange = e => {
    e.preventDefault();
    const newDisclaimer = e.target.value;
    debounced(wire.id, newDisclaimer);
    set(state => {
      state.wire.disclaimer = newDisclaimer;
    });
    setOriginalDisclaimer(newDisclaimer);
  };

  const deleteDisclaimer = e => {
    e.preventDefault();
    updateDisclaimer(wire.id, '');
    set(state => {
      state.wire.disclaimer = '';
    });
    setDeleted(true);
  };

  const restoreDisclaimer = e => {
    e.preventDefault();
    updateDisclaimer(wire.id, originalDisclaimer);
    set(state => {
      state.wire.disclaimer = originalDisclaimer;
    });
    setDeleted(false);
  };

  let actionLabel;
  if (editable) {
    actionLabel = 'Done';
  } else {
    actionLabel = originalDisclaimer ? 'Edit Disclaimer' : 'Add Disclaimer';
  }

  return (
    <ThemeProvider theme={theme}>
      <Container>
        <TitleContainer>
          <Title>Disclaimer</Title>
          <Link onClick={toggleEditable}>{actionLabel}</Link>
          <Loading ref={loadingRef} variant="inline" />
          { deleted ? <RestoreLink onClick={restoreDisclaimer}>Restore</RestoreLink>
            : <Button className="fa fa-trash-o" onClick={deleteDisclaimer} /> }
        </TitleContainer>
        { editable ? (
          <>
            <ContentEditable
              ref={contentRef}
              value={wire.disclaimer}
              onChange={onChange}
            />
            <BottomLink onClick={toggleEditable}>{actionLabel}</BottomLink>
          </>
        ) : <Content dangerouslySetInnerHTML={{ __html: wire.disclaimer }} /> }
      </Container>
    </ThemeProvider>
  );
}
